import { FileOpener } from '@capacitor-community/file-opener'
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem'
import { ColoursObj, MainButton } from '@eventr/common-storybook'
import { Box, Chip, Stack } from '@mui/material'
import { computed, signal } from '@preact/signals-core'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { NavigationView } from '../../layout/NavigationView'
import { LogCard } from './logCard'
import { LogDrawer } from './logDrawer'

export interface FlutterLog {
    timeStamp: number
    message: string
    tags: string[]
    extraArgs: any[]
}

export const logsSignal = signal<FlutterLog[]>([])

const tags = computed(() => {
    const allTags = logsSignal.value.flatMap((log) => log.tags)
    return Array.from(new Set(allTags))
})

const activeTags = signal<string[]>([])

const logsFilteredByTag = computed(() => {
    if (activeTags.value.length === 0) return logsSignal.value.reverse()

    return logsSignal.value
        .filter((log) => {
            return log.tags.some((t) => activeTags.value.includes(t))
        })
        .reverse()
})

export const LogsPage: FC = () => {
    const nav = useNavigate()

    const onTagClick = (tag: string) => {
        if (activeTags.value.includes(tag)) {
            activeTags.value = activeTags.peek().filter((t) => t !== tag)
            return
        }
        activeTags.value = [...activeTags.peek(), tag]
    }
    const tagStyle = (tag: string) => {
        if (activeTags.value.includes(tag)) {
            return {
                backgroundColor: ColoursObj.core.primary,
                color: 'white',
            }
        }
        return {
            backgroundColor: 'white',
            color: 'black',
        }
    }
    return (
        <Box paddingTop={'10px'}>
            <NavigationView
                navigationBarTitle="Logs"
                backButtonAction={() => {
                    nav(-1)
                }}
                usePageTransition
            >
                <Stack
                    width={'100vw'}
                    overflow={'auto'}
                    whiteSpace={'nowrap'}
                    direction={'row'}
                >
                    {tags.value.map((t, i) => {
                        return (
                            <Chip
                                key={i}
                                label={t}
                                clickable={false}
                                sx={tagStyle(t)}
                                variant="outlined"
                                onClick={() => {
                                    onTagClick(t)
                                }}
                            />
                        )
                    })}
                </Stack>
                <MainButton
                    onClick={async () => {
                        const date = new Date()
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(
                            2,
                            '0'
                        )
                        const day = String(date.getDate()).padStart(2, '0')
                        const hour = String(date.getHours()).padStart(2, '0')
                        const minute = String(date.getMinutes()).padStart(
                            2,
                            '0'
                        )

                        const dateString = `${year}${month}${day}${hour}${minute}`

                        const file = await Filesystem.writeFile({
                            path: `Eventr-logs-${dateString}.txt`,
                            data: JSON.stringify(logsSignal.value),
                            directory: Directory.Documents,
                            recursive: true,
                            encoding: Encoding.UTF8,
                        })
                        FileOpener.open({ filePath: file.uri })
                    }}
                >
                    Save logs to file
                </MainButton>

                <Stack padding={'20px'}>
                    {logsFilteredByTag.value.map((log, i) => {
                        return <LogCard key={i} log={log} />
                    })}
                </Stack>
            </NavigationView>
            <LogDrawer />
        </Box>
    )
}
